import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
  getReferralUserDetailApi,
  ReferralUserDetail,
  InviteeStatus,
} from "../../features/referrals/api/referrals.api"
import { ChevronLeftIcon } from "@heroicons/react/16/solid"
import { UsersIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"

const LIMIT = 20

const STATUS_CONFIG: Record<InviteeStatus, { label: string; className: string }> = {
  clicked:    { label: "Перешёл по ссылке", className: "bg-gray-100 text-gray-500 ring-1 ring-gray-200" },
  registered: { label: "Зарегистрирован",   className: "bg-blue-50 text-blue-700 ring-1 ring-blue-200" },
  activated:  { label: "Активирован",       className: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" },
}

const StatusBadge = ({ status }: { status: InviteeStatus }) => {
  const cfg = STATUS_CONFIG[status]
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  )
}

const formatDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"

const StatCard = ({ label, value }: { label: string; value: string | number }) => (
  <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4">
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{typeof value === "number" ? value.toLocaleString("ru") : value}</p>
  </div>
)

export const ReferralUserDetailPage = () => {
  const { userId } = useParams<{ userId: string }>()
  const [data, setData] = useState<ReferralUserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [page, setPage] = useState(1)

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    setError(false)
    getReferralUserDetailApi(userId, { limit: LIMIT, offset: (page - 1) * LIMIT })
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [userId, page])

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <Link to="/admin/referrals/top"
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors">
          <ChevronLeftIcon className="w-4 h-4" />
          Топ рефереров
        </Link>
        <span className="text-gray-300">/</span>
        <h2 className="text-base font-semibold text-gray-800 truncate font-mono">{userId?.slice(0, 16)}…</h2>
      </div>

      {error ? (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Не удалось загрузить данные по пользователю</p>
        </div>
      ) : loading || !data ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white border border-gray-100 rounded-2xl h-20 animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Реферальный код" value={data.referral_code} />
            <StatCard label="Клики" value={data.total_clicks} />
            <StatCard label="Регистрации" value={data.total_registered} />
            <StatCard label="Активации" value={data.total_activated} />
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b bg-gray-50/60 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Приглашённые</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/40">
                    {["Пользователь", "Статус", "Клик", "Регистрация", "Активация"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.invitees.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-16 text-center">
                        <UsersIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Пока никого не пригласил</p>
                      </td>
                    </tr>
                  ) : (
                    data.invitees.map((inv, i) => (
                      <tr key={inv.referred_user_id || `clicked-${i}`} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-4 py-3.5 font-mono text-xs text-gray-500">
                          {inv.referred_user_id ? `${inv.referred_user_id.slice(0, 14)}…` : <span className="text-gray-300 italic">—</span>}
                        </td>
                        <td className="px-4 py-3.5"><StatusBadge status={inv.status} /></td>
                        <td className="px-4 py-3.5 text-xs text-gray-500">{formatDate(inv.clicked_at)}</td>
                        <td className="px-4 py-3.5 text-xs text-gray-500">{formatDate(inv.registered_at)}</td>
                        <td className="px-4 py-3.5 text-xs text-gray-500">{formatDate(inv.activated_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-3.5 border-t bg-gray-50/40 flex items-center justify-between">
              <span className="text-xs text-gray-400">Страница <span className="font-medium text-gray-600">{page}</span></span>
              <div className="flex gap-1.5">
                <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  ← Пред.
                </button>
                <button disabled={data.invitees.length < LIMIT} onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  След. →
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
